/*
  Violet — the shade applier, sitewide (formerly kustomer-shade-match.js).

  Takes a match ({handle, shade} pairs) and selects those shades on whatever
  the page shows: the current product's picker, product cards on a grid, or
  any select as a last resort, then marks the matched shade with Violet's
  heart. Two sources feed it: the sitewide record in localStorage
  ('subtl:shade', written by the Violet flyout) and, until Siena is switched
  off, the Kustomer chat path polling the old subtl-shade-match Worker. The
  Violet page bridge (assets/violet-page.js) calls window.SubtlShadeApply
  for a match made on the page itself.
*/
(function initShadeMatchAutoSelect() {

  // Set DEBUG = true in browser console to re-enable logging: window.__SHADE_DEBUG = true
  var DEBUG = false;

  // The Siena-era Worker, polled only while a Kustomer conversation exists.
  // Goes away with Siena.
  var WORKER_URL = 'https://subtl-shade-match.rachel-08e.workers.dev';

  function log() {
    if (DEBUG || window.__SHADE_DEBUG) console.log.apply(console, ['[ShadeMatch]'].concat(Array.prototype.slice.call(arguments)));
  }

  // ── State ──────────────────────────────────────────────────────────────────
  var appliedConversationId = '';
  // Cached recs from the most recent successful /shades response. Used to
  // reapply when the quiz reveals its result products (see setupQuizReapply).
  var lastFetchedRecs = null;

  function getConversationId() {
    try {
      var state = JSON.parse(localStorage.getItem('kustomerState') || '{}');
      return state.lastOpenConversationId || '';
    } catch (e) { return ''; }
  }

  function getCurrentProductHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // ── Worker polling ─────────────────────────────────────────────────────────
  function pollWorker() {
    // With Violet on the page, Siena's old recommendations never apply: a
    // Kustomer conversation (Violet's own handoff opens one) would otherwise
    // pull months-old shades over the match Violet just made.
    if (document.querySelector('[data-violet-config]')) return;
    var convId = getConversationId();
    if (!convId || convId === appliedConversationId) return;

    fetch(WORKER_URL + '/shades?conversationId=' + encodeURIComponent(convId))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        log('Worker response:', JSON.stringify(data));
        if (!data.found || !data.recommendations || !data.recommendations.length) return;

        appliedConversationId = convId;
        lastFetchedRecs = data.recommendations;
        saveSiteRecord('chat', data.recommendations);
        applyRecommendations(data.recommendations);
      })
      .catch(function (e) { log('Worker poll error:', e.message); });
  }

  setInterval(pollWorker, 2000);

  // ── The sitewide shade record ──────────────────────────────────────────────
  // localStorage 'subtl:shade': { by: 'violet'|'chat', concealer, match_id, at,
  // recommendations: [{ handle, shade }] }. Written by the Violet flyout on the
  // SSD landing page and by the chat path above, read here on every other page
  // so the matched shades are preselected wherever the shopper goes. The
  // landing page applies its own match, so it is skipped.
  var SITE_RECORD_KEY = 'subtl:shade';
  var siteRecordApplied = false;

  function saveSiteRecord(by, recs) {
    try { localStorage.setItem(SITE_RECORD_KEY, JSON.stringify({ by: by, at: Date.now(), recommendations: recs })); } catch (e) { /* private mode */ }
  }

  function applySiteRecord() {
    if (siteRecordApplied) return;
    if (document.querySelector('[data-ssd10-state]')) return;   // the landing page drives itself
    var rec = null;
    try { rec = JSON.parse(localStorage.getItem(SITE_RECORD_KEY) || 'null'); } catch (e) { rec = null; }
    if (!rec || !Array.isArray(rec.recommendations) || !rec.recommendations.length) return;
    siteRecordApplied = true;
    lastFetchedRecs = rec.recommendations;
    log('Applying the sitewide shade record from', rec.by);
    applyRecommendations(rec.recommendations);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applySiteRecord);
  else applySiteRecord();
  // Product grids and pickers often render after load (the Build My Stak
  // grid fills in over a few seconds), so the record is applied again at a
  // few beats and whenever new swatches appear, until the shopper picks a
  // shade by hand: from then on their choice stands.
  var shopperPicked = false;
  document.addEventListener('click', function (e) {
    if (!e.isTrusted) return;
    if (e.target.closest && e.target.closest('label, input[type="radio"][name^="option"], .product-swatches-options__item, button[data-option-value]')) shopperPicked = true;
  }, true);
  function reapplySiteRecord() { if (shopperPicked) return; siteRecordApplied = false; applySiteRecord(); }
  [1500, 4000, 8000].forEach(function (ms) { setTimeout(reapplySiteRecord, ms); });
  if (typeof MutationObserver === 'function') {
    var pending = null;
    var mo = new MutationObserver(function (muts) {
      if (shopperPicked) { mo.disconnect(); return; }
      var swatchesAdded = muts.some(function (m) {
        return Array.prototype.some.call(m.addedNodes || [], function (n) {
          return n.nodeType === 1 && (n.matches('input[type="radio"][name^="option"], button[data-option-value]') || (n.querySelector && n.querySelector('input[type="radio"][name^="option"], button[data-option-value]')));
        });
      });
      if (!swatchesAdded) return;
      clearTimeout(pending); pending = setTimeout(reapplySiteRecord, 400);
    });
    mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 30000);
  }

  // Trigger an immediate poll on any Kustomer SDK event
  function waitForKustomer(cb, attempts) {
    attempts = attempts || 0;
    var core = window.KustomerCore || window.Kustomer;
    if (core && core.addListener) {
      log('Kustomer SDK ready');
      return cb(core);
    }
    if (attempts > 40) { log('Kustomer SDK not found after 20s'); return; }
    setTimeout(function () { waitForKustomer(cb, attempts + 1); }, 500);
  }

  waitForKustomer(function (core) {
    ['onUnread', 'onConversationCreate'].forEach(function (evt) {
      try {
        core.addListener(evt, function () { pollWorker(); });
      } catch (e) {
        log(evt + ' registration failed:', e.message);
      }
    });
  });

  // ── Quiz reveal: reapply when .quiz-result-products mutates ───────────────
  // On the quiz page, .quiz-result-products is in the DOM on load but its
  // product cards are gated until the user finishes the quiz or clicks
  // "Get Result". At that point the quiz JS injects/reveals cards and sets
  // its own default shades, which would override anything we applied
  // earlier. We watch the container for DOM changes and re-apply the
  // cached recs (debounced). Each apply branch is idempotent against an
  // already-selected swatch, so this won't loop.
  function setupQuizReapply(attempts) {
    attempts = attempts || 0;
    if (!isQuizPage()) return;
    var container = document.querySelector('.quiz-result-products');
    if (!container) {
      if (attempts > 30) { log('Quiz: .quiz-result-products never appeared, giving up'); return; }
      setTimeout(function () { setupQuizReapply(attempts + 1); }, 1000);
      return;
    }

    var debounceTimer = null;
    var observer = new MutationObserver(function () {
      if (!lastFetchedRecs || !lastFetchedRecs.length) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        log('Quiz: DOM change in .quiz-result-products, reapplying recs');
        applyRecommendations(lastFetchedRecs);
      }, 500);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
    log('Quiz reapply observer attached');

    // Once the shopper makes their OWN (trusted) shade choice, stop reapplying — otherwise
    // the observer snaps every manual selection back to the matched shade. The worker's own
    // programmatic clicks have isTrusted === false, so they don't trigger this.
    container.addEventListener('click', function (e) {
      if (!e.isTrusted) return;
      if (e.target.closest('label, input[type="radio"][name^="option"], .product-swatches-options__item, button[data-option-value]')) {
        observer.disconnect();
        log('Quiz: shopper picked a shade — reapply observer disconnected');
      }
    }, true);
  }
  setupQuizReapply();

  // ── Apply all recommendations ──────────────────────────────────────────────

  // On the quiz result page only BB Powder and Concealer are overridden;
  // everything else keeps the quiz's own recommendation.
  var QUIZ_HANDLES = ['bb-powder', 'concealer'];

  function isQuizPage() {
    return window.location.pathname.indexOf('/pages/quiz') !== -1;
  }

  // Violet's sitewide bridge (assets/violet-page.js) applies a fresh match
  // through the same applier, so a match made in the flyout lands on the
  // page it was made on, not only on the next one.
  window.SubtlShadeApply = { apply: function (recs) { if (Array.isArray(recs) && recs.length) applyRecommendations(recs); }, save: saveSiteRecord };

  function applyRecommendations(recommendations) {
    log('Applying', recommendations.length, 'recommendations');

    // The quiz results page used to take only the concealer and BB powder
    // from a chat match, since the quiz picked the rest itself. Violet
    // matches every product, so with her on the page the whole record
    // applies there too.
    if (isQuizPage() && !document.querySelector('[data-violet-config]')) {
      recommendations = recommendations.filter(function (rec) {
        return QUIZ_HANDLES.some(function (h) { return rec.handle.indexOf(h) !== -1; });
      });
      log('Quiz page — filtered to', recommendations.length, 'recommendations');
    }

    var currentHandle = getCurrentProductHandle();

    recommendations.forEach(function (rec) {
      var applied = false;

      // PDP: current page is this product — use dropdown/swatch
      if (currentHandle && currentHandle === rec.handle) {
        applied = applyShadeOnPDP(rec.shade);
      }

      // Grid: find product cards via links and click their swatch
      if (!applied) {
        var gridApplied = applyShadeOnGrid(rec);
        if (gridApplied) applied = true;
      }

      if (!applied) {
        applyShadeGlobally(rec.shade);
      }
    });

  }

  // ── Matched-shade signal ───────────────────────────────────────────────────

  // Tell the page which shade we just matched, so UI that distinguishes a matched
  // shade from a shopper's own pick can label it — currently the premade PDP's
  // "matched · tap to change" hint (snippets/product-block-variant-picker-select.liquid).
  // Dispatched on the swatch itself and bubbled, so the listener can scope the
  // update to that swatch's own option.
  function announceMatched(el, shade) {
    if (!el || !shade) return;
    markMatched(el, shade);
    try {
      el.dispatchEvent(new CustomEvent('subtl:shade-matched', {
        bubbles: true,
        detail: { shade: shade }
      }));
    } catch (e) {
      log('announceMatched failed:', e.message);
    }
  }

  // ── The match mark ─────────────────────────────────────────────────────────
  // Violet's orange heart on the matched shade, wherever a shade is shown:
  // a swatch button, a grid swatch label, a dropdown row, and the closed
  // dropdown while the matched shade is the one selected. It marks the
  // match, not the selection, so it stays put when the shopper taps another
  // shade. The heart itself comes from the flyout's config (rendered by
  // snippets/violet-mark.liquid); without Violet on the page, nothing.
  var heartMarkup = null;
  function heartSvg() {
    if (heartMarkup !== null) return heartMarkup;
    heartMarkup = '';
    try {
      var cfgEl = document.querySelector('[data-violet-config]');
      var cfg = cfgEl ? JSON.parse(cfgEl.textContent) : null;
      if (cfg && cfg.marks && cfg.marks.heart) heartMarkup = cfg.marks.heart;
    } catch (e) { heartMarkup = ''; }
    return heartMarkup;
  }
  function heartBadge(shade) {
    var svg = heartSvg(); if (!svg) return null;
    var b = document.createElement('span');
    b.className = 'subtl-match-heart';
    b.setAttribute('data-match-shade', shade);
    b.setAttribute('role', 'img');
    b.setAttribute('aria-label', 'Your Violet match');
    b.innerHTML = svg;
    return b;
  }
  // The visual the badge sits on: a pan image or swatch inside the element,
  // else the element itself (a round swatch button or label).
  function badgeAnchor(el) {
    return el.querySelector('img, .swatch, [class*="swatch-img"], [class*="option-img"]') || el;
  }
  function placeBadge(el, shade) {
    var old = el.querySelector(':scope > .subtl-match-heart'); if (old) old.remove();
    var badge = heartBadge(shade); if (!badge) return;
    el.classList.add('is-violet-matched');
    var anchor = badgeAnchor(el);
    if (anchor !== el) {
      // Just inside the anchor's top-right corner, measured within the element.
      var r = anchor.getBoundingClientRect(), p = el.getBoundingClientRect();
      badge.classList.add('subtl-match-heart--inline');
      badge.style.left = Math.round(r.right - p.left - 14) + 'px';
      badge.style.top = Math.round(r.top - p.top) + 'px';
    }
    el.appendChild(badge);
  }
  function scopeOf(el) {
    return el.closest('.product__option, .product__color-swatches, .product-swatches-options, .product__variant-data, form, article, li, [data-product]') || document;
  }
  function markMatched(el, shade) {
    if (!el || !shade || !heartSvg()) return;
    var scope = scopeOf(el);
    // One heart per picker.
    var hearts = scope.querySelectorAll('.subtl-match-heart');
    for (var i = 0; i < hearts.length; i++) { var host = hearts[i].parentElement; hearts[i].remove(); if (host) host.classList.remove('is-violet-matched'); }
    placeBadge(el, shade);
    // The closed dropdown (premade pickers) shows the selected shade; it
    // carries the heart while that is the matched one.
    var wrap = el.closest('.product__variant-data');
    var trigger = wrap && wrap.querySelector('button[data-variant-trigger], .product__dynamic-swatches-trigger');
    if (trigger) {
      var sync = function () {
        var sel = wrap.querySelector('button[data-option-value].selected');
        var on = !!sel && (sel.getAttribute('data-option-value') || '') === shade;
        var cur = trigger.querySelector(':scope > .subtl-match-heart, .product__dynamic-swatches-trigger-content > .subtl-match-heart');
        if (on && !cur) placeBadge(trigger.querySelector('.product__dynamic-swatches-trigger-content') || trigger, shade);
        else if (!on && cur) { var h = cur.parentElement; cur.remove(); if (h) h.classList.remove('is-violet-matched'); }
      };
      window.setTimeout(sync, 0);
      if (!wrap.__violetHeartSync) {
        wrap.__violetHeartSync = true;
        wrap.addEventListener('click', function () { window.setTimeout(sync, 0); });
      }
    }
  }
  // The label of a grid radio (buildmystack, collection cards): the swatch is the label.
  function markRadio(radio, shade) {
    var label = radio && document.querySelector('label[for="' + radio.id + '"]');
    if (label) markMatched(label, shade || radio.value);
  }

  // ── Radio click + overflow expand helper ──────────────────────────────────

  // Grid swatch lists hide swatches past N (6 desktop / 4 mobile, varies by
  // breakpoint) via CSS, with a "+X" overflow-note that the theme's
  // custom-events.js binds a click handler to which un-hides everything. When
  // our matched swatch is in the hidden tail, we click that note first so the
  // selected swatch is actually visible to the user, then click the label.
  function clickRadioLabel(radio) {
    if (!radio) return false;
    var li = radio.closest('li');
    if (li && window.getComputedStyle(li).display === 'none') {
      var list = li.parentElement;
      var overflow = list && list.querySelector('.product-swatches-options__item-overflow-note');
      if (overflow) {
        log('Expanding overflow note to reveal swatch');
        overflow.click();
      }
    }
    var label = document.querySelector('label[for="' + radio.id + '"]');
    if (!label) return false;
    label.click();

    // In the quiz results grid only the swatch with `.rec` is shown (theme CSS hides the
    // rest). The quiz puts `.rec` on its own recommended shade; when we override that shade,
    // move `.rec` to the swatch we just selected so the SHOWN swatch matches the selected
    // shade. Scoped to the quiz grid — `.rec` is meaningless on other pages.
    if (isQuizPage()) {
      var selectedLi = radio.closest('li');
      if (selectedLi) {
        var swatchList = selectedLi.parentElement;
        if (swatchList) {
          var currentRec = swatchList.querySelectorAll('li.rec');
          for (var c = 0; c < currentRec.length; c++) {
            if (currentRec[c] !== selectedLi) currentRec[c].classList.remove('rec');
          }
        }
        selectedLi.classList.add('rec');
      }
    }
    return true;
  }

  // ── PDP: swatch button or hidden select ───────────────────────────────────

  function applyShadeOnPDP(shadeName) {
    return applyShadeViaSwatch(document, shadeName) || applyShadeViaSelect(document, shadeName);
  }

  // ── Grid: find the product card and click its swatch ─────────────────────

  function applyShadeOnGrid(rec) {
    var needle = rec.shade.trim().toUpperCase();
    var links = document.querySelectorAll('a[href*="/products/' + rec.handle + '"]');
    var applied = false;
    var seen = [];

    for (var i = 0; i < links.length; i++) {
      // Menu and footer links to the product are not product cards; walking
      // up from one of those reaches the whole page and its other products.
      if (links[i].closest('nav, header, footer, [class*="navigation"], [class*="menu"]')) continue;
      var card = links[i].closest(
        'li, article, [data-product], .product-item, .product-card, .grid__item, ' +
        '.product-row, [class*="stak-item"], [class*="bundle-item"], [class*="kit-item"]'
      );

      // Fallback: walk up the DOM looking for a container that has swatches or selects
      if (!card) {
        var el = links[i].parentElement;
        for (var k = 0; k < 6 && el; k++) {
          if (el.querySelector('button[data-option-value], select[name^="options["], input[type="radio"][name^="option"]')) {
            card = el;
            break;
          }
          el = el.parentElement;
        }
      }

      if (!card || seen.indexOf(card) !== -1) continue;
      seen.push(card);

      // 1. button[data-option-value] (PDP-style swatches)
      var swatches = card.querySelectorAll('button[data-option-value]');
      for (var j = 0; j < swatches.length; j++) {
        var val = (swatches[j].getAttribute('data-option-value') || '').trim().toUpperCase();
        if (val === needle || val.indexOf(needle) === 0) {
          log('Grid swatch click:', rec.shade, 'on', rec.handle);
          swatches[j].click();
          announceMatched(swatches[j], swatches[j].getAttribute('data-option-value'));
          applied = true;
          break;
        }
      }

      // 2. input[type="radio"] + label (buildmystack-style grid swatches)
      if (!applied) {
        var radios = card.querySelectorAll('input[type="radio"][name^="option"]');
        for (var r = 0; r < radios.length; r++) {
          var radioVal = (radios[r].value || '').trim().toUpperCase();
          if (radioVal === needle || radioVal.indexOf(needle) === 0) {
            if (radios[r].checked) {
              log('Grid: radio already selected for', rec.shade, 'on', rec.handle);
              applied = true; // already correct — no click needed
            } else {
              log('Grid radio click:', radios[r].value, 'on', rec.handle);
              if (clickRadioLabel(radios[r])) applied = true;
            }
            markRadio(radios[r], radios[r].value);
            break;
          }
        }
      }

      // 3. select[name^="options["] (bundle page dropdowns)
      if (!applied) {
        var selects = card.querySelectorAll('select[name^="options["]');
        for (var s = 0; s < selects.length; s++) {
          var select = selects[s];
          for (var o = 0; o < select.options.length; o++) {
            var opt = select.options[o];
            var optVal = opt.value.trim().toUpperCase();
            var optTxt = opt.text.trim().toUpperCase();
            if (optVal === needle || optTxt === needle || optVal.indexOf(needle) === 0 || optTxt.indexOf(needle) === 0) {
              log('Grid select match:', opt.value, 'on', rec.handle);
              select.value = opt.value;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              applied = true;
              break;
            }
          }
          if (applied) break;
        }
      }

      if (!applied) {
        log('Grid: no match for', rec.shade, 'in', rec.handle,
          '| buttons:', swatches.length,
          '| radios:', card.querySelectorAll('input[type="radio"][name^="option"]').length,
          '| selects:', card.querySelectorAll('select[name^="options["]').length);
      }
    }

    return applied;
  }

  // ── Global fallback: scan all selects on page by shade name ──────────────

  function applyShadeGlobally(shadeName) {
    var needle = shadeName.trim().toUpperCase();

    // Premade/bundle pages (e.g. starter-stak-deluxe) use a custom dropdown
    // with buttons inside a hidden panel. Clicking the button triggers the
    // theme JS that updates the visible label and underlying select.
    var buttons = document.querySelectorAll('button[data-option-value]');
    for (var i = 0; i < buttons.length; i++) {
      var val = (buttons[i].getAttribute('data-option-value') || '').trim().toUpperCase();
      if (val === needle || val.indexOf(needle) === 0) {
        if (buttons[i].classList.contains('selected')) {
          // Already on the matched shade — no click needed, but it IS the match, so the
          // "matched" label still has to be applied.
          log('Global: swatch already selected for', shadeName);
          announceMatched(buttons[i], buttons[i].getAttribute('data-option-value'));
          return false;
        }
        log('Global swatch click:', buttons[i].getAttribute('data-option-value'));
        buttons[i].click();
        announceMatched(buttons[i], buttons[i].getAttribute('data-option-value'));
        return true;
      }
    }

    // Grid swatches on featured-collection / buildmystack cards are radio
    // inputs (input[name="option-0"][value="Shade Name"]) with a label that
    // triggers the variant change on click.
    var radios = document.querySelectorAll('input[type="radio"][name^="option"]');
    for (var r = 0; r < radios.length; r++) {
      var radioVal = (radios[r].value || '').trim().toUpperCase();
      if (radioVal === needle || radioVal.indexOf(needle) === 0) {
        if (radios[r].checked) {
          log('Global: radio already selected for', shadeName);
          markRadio(radios[r], radios[r].value);
          return false;
        }
        log('Global radio click:', radios[r].value);
        if (clickRadioLabel(radios[r])) { markRadio(radios[r], radios[r].value); return true; }
      }
    }

    // Fallback: change a <select> element directly (covers simpler bundle setups).
    var selects = document.querySelectorAll('select');
    for (var s = 0; s < selects.length; s++) {
      var select = selects[s];
      for (var j = 0; j < select.options.length; j++) {
        var opt = select.options[j];
        var optVal = opt.value.trim().toUpperCase();
        var optTxt = opt.text.trim().toUpperCase();
        if (optVal === needle || optTxt === needle || optVal.indexOf(needle) === 0 || optTxt.indexOf(needle) === 0) {
          if (select.value.trim().toUpperCase() === optVal) {
            log('Global: shade already set to', opt.value);
            return false;
          }
          log('Global select match:', opt.value);
          select.value = opt.value;
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }

    log('Global: no match for', shadeName);
    return false;
  }

  // ── Shared shade helpers ───────────────────────────────────────────────────

  function applyShadeViaSwatch(scope, shadeName) {
    var needle = shadeName.trim().toUpperCase();
    var buttons = scope.querySelectorAll('button[data-option-value]');
    for (var i = 0; i < buttons.length; i++) {
      var val = (buttons[i].getAttribute('data-option-value') || '').trim().toUpperCase();
      if (val === needle || val.indexOf(needle) === 0) {
        log('PDP swatch click:', buttons[i].getAttribute('data-option-value'));
        buttons[i].click();
        announceMatched(buttons[i], buttons[i].getAttribute('data-option-value'));
        buttons[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }
    }
    return false;
  }

  function applyShadeViaSelect(scope, shadeName) {
    var needle = shadeName.trim().toUpperCase();
    var selects = scope.querySelectorAll('select[name^="options["]');
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      for (var j = 0; j < select.options.length; j++) {
        var o = select.options[j];
        var val = o.value.trim().toUpperCase();
        var txt = o.text.trim().toUpperCase();
        if (val === needle || txt === needle || val.indexOf(needle) === 0 || txt.indexOf(needle) === 0) {
          log('PDP select match:', o.value);
          select.value = o.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
      }
    }
    return false;
  }

  // The "Shades selected for you" toast from the Siena days is gone: the
  // record is applied a few times as grids fill in, and it fired each time.
  // Violet's flyout and the hearts on the swatches say what was matched.

})();
